/* =========================================================
   Seven Realms VTable (Assets Version)
   - Single init (no double binding)
   - Delegated click handling for steppers + ATC
   - Works with multiple tables on a page
   - Creates toast container once if missing
   - Updates cart count + attempts to open cart drawer
   ========================================================= */

(function () {
  'use strict';

  if (window.__innernerd_vtable_assets_init) return;
  window.__innernerd_vtable_assets_init = true;

  const CONFIG = {
    toastDuration: 2800,
    successStateDuration: 1800,
    stockRefreshCooldown: 30000
  };

  function ensureToast() {
    let toast = document.getElementById('innernerd-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'innernerd-toast';
      toast.className = 'in-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toast);
    }
    return toast;
  }

  function showToast(message, type) {
    const toast = ensureToast();
    toast.textContent = message;
    toast.className = 'in-toast is-visible';

    if (type === 'error') toast.classList.add('in-toast--error');
    if (type === 'success') toast.classList.add('in-toast--success');

    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), CONFIG.toastDuration);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return await res.json();
  }

  async function updateCartCount() {
    try {
      const cart = await fetchCart();
      if (window.InnerNerdCartSync) {
        window.InnerNerdCartSync.update(cart);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (e) {
      // silent
    }
  }

  function openCartDrawerBestEffort() {
    // Method 1: Seven Realms custom drawer API
    if (window.InnerNerdCartDrawer && typeof window.InnerNerdCartDrawer.open === 'function') {
      window.InnerNerdCartDrawer.open();
      return true;
    }

    // Method 2: Theme cart drawer API
    if (window.theme && window.theme.cart && typeof window.theme.cart.open === 'function') {
      window.theme.cart.open();
      return true;
    }

    // Method 3: Data trigger
    const trigger = document.querySelector('[data-cart-drawer-open]');
    if (trigger) {
      trigger.click();
      return true;
    }

    // Method 4: Dawn-style components
    const drawer = document.querySelector('cart-drawer, cart-notification');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    return false;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function getRowMaxQty(row) {
    const maxQty = parseInt(row?.getAttribute('data-max-qty') || '999', 10);
    return Number.isFinite(maxQty) ? maxQty : 999;
  }

  function getRowQty(row) {
    const valEl = row?.querySelector('.in-stepper__val');
    const current = parseInt(valEl?.textContent || '1', 10);
    return Number.isFinite(current) ? current : 1;
  }

  function setRowQty(row, qty) {
    const valEl = row?.querySelector('.in-stepper__val');
    if (valEl) valEl.textContent = String(qty);

    const dec = row?.querySelector('.in-stepper__btn--dec');
    const inc = row?.querySelector('.in-stepper__btn--inc');
    const maxQty = getRowMaxQty(row);

    if (dec) {
      dec.disabled = qty <= 1;
      dec.setAttribute('aria-disabled', qty <= 1 ? 'true' : 'false');
    }
    if (inc) {
      inc.disabled = qty >= maxQty;
      inc.setAttribute('aria-disabled', qty >= maxQty ? 'true' : 'false');
    }
  }

  async function addToCart(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity })
    });

    if (!res.ok) {
      let msg = 'Failed to add to cart';
      try {
        const err = await res.json();
        msg = err?.description || err?.message || msg;
      } catch (_) {}
      throw new Error(msg);
    }

    return await res.json();
  }

  function handleStepperClick(btn) {
    const row = btn.closest('tr');
    if (!row) return;

    const maxQty = getRowMaxQty(row);
    const current = getRowQty(row);
    const isInc = btn.classList.contains('in-stepper__btn--inc');

    const next = clamp(isInc ? current + 1 : current - 1, 1, maxQty);
    setRowQty(row, next);
  }

  async function handleATCClick(btn) {
    const row = btn.closest('tr');
    if (!row) return;

    const variantId = btn.getAttribute('data-variant-id');
    const addLabel = btn.getAttribute('data-add-label') || 'Add to Cart';
    const qty = getRowQty(row);

    if (!variantId) {
      showToast('Error: Missing variant ID', 'error');
      return;
    }

    if (btn.classList.contains('is-loading') || btn.disabled) return;

    btn.classList.add('is-loading');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      await addToCart(variantId, qty);

      // Success UI
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      btn.setAttribute('aria-busy', 'false');

      const labelEl = btn.querySelector('.in-atc__label');
      if (labelEl) labelEl.textContent = '✓ Added';

      row.setAttribute('data-selected', '');

      const itemText = qty > 1 ? `${qty} items` : '1 item';
      showToast(`${itemText} added to cart`, 'success');

      await updateCartCount();
      openCartDrawerBestEffort();

      // Reset
      setTimeout(() => {
        btn.classList.remove('is-success');
        if (labelEl) labelEl.textContent = addLabel;
        btn.disabled = false;
        row.removeAttribute('data-selected');
      }, CONFIG.successStateDuration);

      // Dispatch hook for anything else you want to listen to
      document.dispatchEvent(new CustomEvent('innernerd:cartAdded', {
        detail: { variant_id: variantId, quantity: qty },
        bubbles: true
      }));
    } catch (e) {
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');
      btn.disabled = false;
      btn.setAttribute('aria-busy', 'false');

      showToast(e.message || 'Failed to add item to cart', 'error');

      setTimeout(() => btn.classList.remove('is-error'), 2000);
    }
  }

  // Delegated click handler
  document.addEventListener('click', (e) => {
    const stepBtn = e.target.closest('.in-stepper__btn');
    if (stepBtn && !stepBtn.disabled) {
      handleStepperClick(stepBtn);
      return;
    }

    const atcBtn = e.target.closest('.in-atc.in-atc--active');
    if (atcBtn) {
      handleATCClick(atcBtn);
    }
  });

  // Initialize steppers: ensure correct initial button states
  function initTables() {
    document.querySelectorAll('[data-innernerd-table] tbody tr').forEach((row) => {
      const available = row.getAttribute('data-available') === 'true';
      const maxQty = getRowMaxQty(row);

      // default qty = 1
      setRowQty(row, 1);

      // disable inc/dec if unavailable
      if (!available) {
        row.querySelectorAll('.in-stepper__btn').forEach((b) => {
          b.disabled = true;
          b.setAttribute('aria-disabled', 'true');
        });
      } else {
        // if maxQty is 1, disable inc
        const inc = row.querySelector('.in-stepper__btn--inc');
        if (inc && maxQty <= 1) {
          inc.disabled = true;
          inc.setAttribute('aria-disabled', 'true');
        }
      }
    });
  }

  // Run once DOM is ready (defer should handle, but safe)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTables, { once: true });
  } else {
    initTables();
  }

})();
